import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { bankNameSchema, type BankName } from '@hero/schema';
import { SelectAuto, type SelectAutoOption } from '../Select';
import { FieldShell } from './shared/FieldShell';
import type { RhfFieldBaseProps } from './shared/types';

export type BankNameFieldProps = RhfFieldBaseProps;

const BANK_KEYS = bankNameSchema.options;

/**
 * `BankNameField` — RHF-bound bank picker for
 * `Bank.bankName: BankName`. Pulls the canonical list from
 * `bankNameSchema.options` (`@hero/schema`) so the dropdown
 * never drifts from the backend enum.
 *
 * Label is sourced from `m-organization:form.bank_name` and the
 * per-bank option labels from `m-payment:banks.<key>.title`,
 * mirroring the legacy `feature/Bank/Selector/BankSelector` and
 * keeping the translation surface unchanged for existing call
 * sites.
 */
export function BankNameField(props: BankNameFieldProps) {
  const { name, label, placeholder, helper, required, financing, disabled, className } =
    props;
  const { control } = useFormContext();
  const { t } = useTranslation(['m-organization', 'm-payment']);
  const generatedId = useId();
  const inputId = `field-${name}-${generatedId}`;

  const options: SelectAutoOption[] = BANK_KEYS.map(key => ({
    value: key,
    label: t(`m-payment:banks.${key}.title`),
  }));

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          label={label ?? t('m-organization:form.bank_name.title')}
          helper={helper}
          required={required}
          financing={financing}
          error={fieldState.error?.message}
          htmlFor={inputId}
          className={className}>
          <SelectAuto
            value={(field.value as BankName | undefined) ?? undefined}
            options={options}
            onChange={field.onChange}
            placeholder={placeholder ?? t('m-organization:form.bank_name.placeholder')}
            disabled={disabled}
          />
        </FieldShell>
      )}
    />
  );
}
